import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from "@angular/core";
import { map, tap } from "rxjs/operators";
import {
  CstFilterBuilder,
  ICstFilterGroup,
} from "src/app/common-components/filter";
import { ICstFilterItem } from "src/app/common-components/filter/models/cst-filter-item";
import {
  CstListViewDataSource,
  ICstGuiCommand,
  ICstListViewColumn,
  CstListViewRowStyle,
  ICstListViewState,
  ICstListViewSelectionChanged,
} from "src/app/common-components/list-view/models";
import { CstDisposableContainer } from "src/app/core";
import { UserService } from "src/app/services/user.service";
import {
  AnlageLookupDto,
  AnlagenClientProvider,
  BuildingTypeDto,
  FacilityMonitoringClientProvider,
  MasterDataInformationGetCommand,
  MonitoringStatusDto,
  MonitoringStatusGetQuery,
  WawiMasterDataClientProvider,
} from "src/app/web-api/web-api";

interface IAnlageListFilter {
  facilityNo: string;
  location: string;
  buildingTypeID: number;
  displayInactive: boolean;
}

@Component({
  selector: "app-anlage-api-list",
  templateUrl: "./anlage-list.component.html",
  styleUrls: ["./anlage-list.component.scss"],
  standalone: false,
})
export class AnlageListComponent implements AfterViewInit, OnDestroy {
  private _anlageClient = inject(AnlagenClientProvider);
  private _filterBuilder = inject(CstFilterBuilder);
  private _mdClientProvider = inject(WawiMasterDataClientProvider);
  private _monitoringClient = inject(FacilityMonitoringClientProvider);
  private _userService = inject(UserService);
  private _cd = inject(ChangeDetectorRef);

  @Input() filterStorageKey = "AnlageListFilter";
  @Input() entryClickCommand: ICstGuiCommand<AnlageLookupDto> = null;
  @Input() selectionEnabled = false;
  @Input() actions: ICstGuiCommand<AnlageLookupDto> = null;

  @Output() selectionChanged = new EventEmitter<
    ICstListViewSelectionChanged<AnlageLookupDto>
  >();

  @ViewChild("filterTemplateBuildingType")
  filterTemplateBuildingType: TemplateRef<any>;

  private _columnState: ICstListViewColumn<AnlageLookupDto> = null;
  @ViewChild("columnStateTemplate")
  public columnStateTemplate: TemplateRef<any>;

  public dataSource = new CstListViewDataSource<AnlageLookupDto>({
    pagination: { pageSize: 10 },
  });
  private _columnFacilityNoCombined: ICstListViewColumn<AnlageLookupDto> = {
    field: "facilityNoCombined",
    header: $localize`:@@AnlageApi_List_HeaderFacilityNoInternal:Facility No.`,
    overflow: "ellipsis",
    command: null,
  };
  public columns: ICstListViewColumn<AnlageLookupDto>[] = null;

  public filterGroup: ICstFilterGroup<IAnlageListFilter> = null;

  private _disposables = new CstDisposableContainer();
  private _monitoringStates: { [key: string]: MonitoringStatusDto } = null;
  private _buildingTypes: BuildingTypeDto[];

  public constructor() {
    this.columns = [
      (this._columnState = {
        field: "id",
        header: "",
        width: "88px",
        template: null,
      }),
      this._columnFacilityNoCombined,
      {
        field: "label",
        header: $localize`:@@AnlageApi_List_HeaderLabel:Label`,
        overflow: "ellipsis",
      },
      {
        field: "locationName",
        header: $localize`:@@AnlageApi_List_HeaderLocationName:Location name`,
        overflow: "ellipsis",
      },
      {
        field: "locationStreet",
        header: $localize`:@@AnlageApi_List_HeaderLocationStreetAndNo:Street / No.`,
        overflow: "ellipsis",
      },
      {
        field: "locationPostalCodeCity",
        header: $localize`:@@AnlageApi_List_HeaderLocationPostalCodeAndCity:Postal Code / City`,
        overflow: "ellipsis",
      },
      {
        field: "locationCountry",
        header: $localize`:@@AnlageApi_List_HeaderLocationCountry:Country`,
        overflow: "ellipsis",
      },
      {
        field: "kunde",
        header: $localize`:@@AnlageApi_List_HeaderCustomer:Customer`,
        overflow: "ellipsis",
      },
      {
        field: "buildingType",
        header: $localize`:@@AnlageApi_List_HeaderBuildingType:Building Type`,
        overflow: "ellipsis",
      },
    ];
    this.dataSource.registerFetchDataCallback(this._fetchData.bind(this));
    this.dataSource.registerAfterDataFetchedCallback(
      this._afterDataFetched.bind(this),
    );
    const cmd = new MasterDataInformationGetCommand({
      requestAllBuildingTypes: true,
    });
    this._mdClientProvider.getMasterDataInformation(cmd).subscribe((result) => {
      this._buildingTypes = result.buildingTypes;
      this.filterGroup.updateValueFormats();
    });
  }
  ngAfterViewInit(): void {
    this._columnFacilityNoCombined.command = this.entryClickCommand;
    this._columnState.template = this.columnStateTemplate;

    this.filterGroup = this._filterBuilder
      .group<IAnlageListFilter>({
        facilityNo: {
          label: $localize`:@@AnlageAPI_Facilities_Filter_FacilityNo:Facility No.`,
          initialValue: "",
        },
        location: {
          label: $localize`:@@AnlageAPI_Facilities_Filter_Location:Location`,
          initialValue: "",
        },
        buildingTypeID: {
          label: $localize`:@@AnlageAPI_Facilities_Filter_BuildingType:Building Type`,
          initialValue: null,
          template: this.filterTemplateBuildingType,
          formatValue: (value: number) =>
            value
              ? this._buildingTypes?.find((x) => x.id === value)?.label
              : null,
        },
        displayInactive: {
          label: $localize`:@@AnlageAPI_Facilities_Filter_DisplayInactive:Display Inactive`,
          dataType: "boolean",
          initialValue: false,
          // chipConfig:{
          //   transform: (value: boolean) => {
          //     return value ? [{
          //       value,
          //       label: $localize`:@@AnlageAPI_Facilities_Filter_DisplayInactive:Display Inactive`,
          //     }] : [];
          //   },
          // }
        },
      })
      .withDialogDimensions({ width: "400px" })
      .withStorageKey(
        this._userService.CurrentUser.mandantID + "_" + this.filterStorageKey,
      )
      .build();
    this.filterGroup.filterChanged().subscribe(() => {
      this.dataSource.refresh();
    });
    this._disposables.register(this.filterGroup);

    this.dataSource.refresh();
    this._cd.detectChanges(); /// Muss drin sein, wenn in "ngAfterViewInit" an die Oberfläche gebundene Variablen geändert werden
  }
  ngOnDestroy(): void {
    this._disposables.dispose();
  }

  public getMonitoringStatus(item: AnlageLookupDto) {
    if (!item?.monitoringBlackboxID) {
      return null;
    }
    return this._monitoringStates[item.monitoringBlackboxID];
  }

  public getRowStyle(element: AnlageLookupDto): CstListViewRowStyle {
    return element.isActive ? "default" : "disabled";
  }

  private _fetchData(state: ICstListViewState) {
    const filter = this.filterGroup.value;
    return this._anlageClient
      .getList(
        filter.facilityNo,
        undefined,
        undefined,
        undefined,
        undefined,
        filter.location,
        [filter.buildingTypeID],
        filter.displayInactive,
        state.pagination.offset,
        state.pagination.pageSize,
      )
      .pipe(
        map((result) => {
          return {
            data: result.data,
            total: result.total,
          };
        }),
      );
  }

  private _afterDataFetched(data: AnlageLookupDto[]) {
    this._monitoringStates = {};
    const ids = data
      .map((x) => x.monitoringBlackboxID)
      .filter((v, i, a) => v && a.indexOf(v) === i);
    if (ids.length === 0) {
      return;
    }
    const query = new MonitoringStatusGetQuery();
    query.init({ iDs: ids });
    this._monitoringClient
      .getStatus(query)
      .pipe(
        tap((result) => {
          for (const status of result) {
            this._monitoringStates[status.blackboxID] = status;
          }
        }),
      )
      .subscribe();
  }
}
